import { useCallback, useState } from 'react';
import { Info, X } from '../../../icons';
import { putRoomNotice, removeRoomNotice } from '../../../api/chat-notice';
import { useChatNoticeStore } from '../../../store/chatNoticeStore';

const MAX_LEN = 500; // 서버 스키마와 같은 값 — 넘으면 400 이 온다.

/**
 * 방 공지 등록·수정 팝업 (room-notice).
 *
 * 껍데기(스크림·헤더·본문)는 정보 패널의 다른 팝업과 **같은 클래스**를 쓴다 — 새 껍데기를
 * 만들면 팝업마다 모양이 갈린다. 본문만 이 파일에서 그린다.
 *
 * 권한: 서버가 **방장(OWNER)만** 등록·삭제를 받는다. 방장이 아니면 읽기 전용으로 띄운다
 * (버튼을 감추기만 하면 서버가 왜 막는지 알 수 없어, 이유를 한 줄로 적는다).
 *
 * 첨부는 없다 — 서버 본문이 글자 한 칸뿐이라 파일을 받을 자리가 없고, 그것이 요청된 사양이다.
 */
export function RoomNoticeDialog({
  roomId,
  canEdit,
  onClose,
}: {
  roomId: string;
  canEdit: boolean;
  onClose: () => void;
}) {
  const notice = useChatNoticeStore((s) => s.noticeByRoom[roomId]);
  const setNotice = useChatNoticeStore((s) => s.setNotice);
  const expand = useChatNoticeStore((s) => s.expand);

  const [text, setText] = useState(notice?.text ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const failMessage = (err: unknown): string => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 403) return '방장만 공지를 등록할 수 있습니다.';
    if (status === 404) return '방을 찾을 수 없습니다.';
    return err instanceof Error ? err.message : '저장하지 못했습니다.';
  };

  const save = useCallback(async () => {
    const body = text.trim();
    if (!body) { setError('공지 내용을 입력하세요.'); return; }
    setBusy(true);
    setError(null);
    try {
      const next = await putRoomNotice(roomId, body);
      setNotice(roomId, next);
      // 새로 등록했으면 접어 둔 기록을 푼다 — 방금 올린 공지가 안 보이면 안 된다.
      expand(roomId);
      onClose();
    } catch (err) {
      setError(failMessage(err));
    } finally {
      setBusy(false);
    }
  }, [text, roomId, setNotice, expand, onClose]);

  const drop = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await removeRoomNotice(roomId);
      setNotice(roomId, null);
      expand(roomId);
      onClose();
    } catch (err) {
      setError(failMessage(err));
    } finally {
      setBusy(false);
    }
  }, [roomId, setNotice, expand, onClose]);

  const title = canEdit ? (notice ? '공지 수정' : '공지 등록') : '방 공지';

  return (
    <div
      className="chat-detail-scrim"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="chat-detail-dlg" role="dialog" aria-modal="true" aria-label={title}>
        <div className="chat-detail-dlghead">
          <span className="chat-detail-title">{title}</span>
          <button
            type="button"
            className="chat-detail-iconbtn chat-detail-close"
            aria-label="닫기"
            onClick={onClose}
          >
            <X size={15} />
          </button>
        </div>

        <div className="chat-detail-dlgbody">
          {canEdit ? (
            <label className="chat-notice-field">
              <span className="chat-notice-flabel">
                공지 내용
                <span className="chat-notice-count">{text.length} / {MAX_LEN}</span>
              </span>
              <textarea
                className="chat-notice-input"
                value={text}
                maxLength={MAX_LEN}
                rows={4}
                placeholder="대화 위에 고정할 내용을 적어 주세요."
                onChange={(e) => setText(e.target.value)}
              />
            </label>
          ) : (
            <p className="chat-notice-read">{notice?.text ?? '등록된 공지가 없습니다.'}</p>
          )}

          <p className="chat-notice-hint">
            <Info size={13} aria-hidden />
            <span>
              {canEdit
                ? '공지는 글자만 등록할 수 있습니다. 파일은 대화에 올려 주세요.'
                : '공지는 방장이 등록합니다.'}
            </span>
          </p>

          {notice?.updatedByName && (
            <p className="chat-notice-by">{notice.updatedByName} 등록</p>
          )}

          {error && <p className="chat-notice-error" role="alert">{error}</p>}

          {canEdit && (
            <div className="chat-notice-actions">
              {notice && (
                <button type="button" className="chat-notice-btn is-drop" onClick={() => void drop()} disabled={busy}>
                  공지 내리기
                </button>
              )}
              <span className="chat-notice-spacer" />
              <button type="button" className="chat-notice-btn" onClick={onClose} disabled={busy}>
                취소
              </button>
              <button type="button" className="chat-notice-btn is-primary" onClick={() => void save()} disabled={busy}>
                {busy ? '저장 중…' : notice ? '수정' : '등록'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
